import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  setDoc,
  query,
} from "firebase/firestore";
import { db } from "../../../../firebase-config";
import { useAuth } from "../../../../provider/Auth.provider";
import { notification } from "antd";
import styles from "./VideoView.module.css";
import { updateDoc, arrayUnion } from "firebase/firestore";
import CertificateDownloadButton from "../UserCertificate/Certificate";
const VideoView = ({ userData, fetchUserData, courseId }) => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [moduleData, setModuleData] = useState(null);
  const [allModules, setAllModules] = useState([]);
  const [packageData, setPackageData] = useState(null);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("description");
  const [note, setNote] = useState("");
  const [userProgress, setUserProgress] = useState({
    overall: 0,
    current: 0,
    watchTime: 0,
  });

  useEffect(() => {
    const preventScreenRecording = () => {
      // Detect screen recording APIs
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        const originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia;
        navigator.mediaDevices.getDisplayMedia = function () {
          notification.warning({
            message: "Screen recording detected",
            description: "Screen recording is not allowed for this content",
            placement: "topRight",
          });
          return Promise.reject(new Error("Screen recording blocked"));
        };
      }

      // Detect visibility changes (screen recording apps often cause this)
      const handleVisibilityChange = () => {
        if (document.hidden) {
          const video = document.querySelector("video");
          if (video) {
            video.pause();
          }
        }
      };

      // Prevent common screen capture shortcuts
      const handleKeyDown = (e) => {
        // Block Print Screen, Windows+G (Game Bar), etc.
        if (
          e.key === "PrintScreen" ||
          (e.metaKey && e.key === "g") ||
          (e.ctrlKey && e.shiftKey && e.key === "I") ||
          e.key === "F12"
        ) {
          e.preventDefault();
          notification.warning({
            message: "Action blocked",
            description: "This action is not allowed",
            placement: "topRight",
          });
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        document.removeEventListener("keydown", handleKeyDown);
      };
    };

    const cleanup = preventScreenRecording();
    return cleanup;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!moduleId) {
        notification.error({
          message: "Module ID not found",
          placement: "topRight",
        });
        navigate("/student/dashboard");
        return;
      }

      try {
        // Fetch current module
        const moduleRef = doc(db, "lms_module", moduleId);
        const moduleSnap = await getDoc(moduleRef);

        if (moduleSnap.exists()) {
          setModuleData({ id: moduleSnap.id, ...moduleSnap.data() });
        } else {
          notification.error({
            message: "Module not found",
            placement: "topRight",
          });
          navigate("/student/dashboard");
          return;
        }

        // Get courseId from URL
        const urlParams = new URLSearchParams(location.search);
        const courseIdFromUrl = urlParams.get("courseId");

        let foundCourse = null;

        if (courseIdFromUrl) {
          const courseRef = doc(db, "lms_course", courseIdFromUrl);
          const courseSnap = await getDoc(courseRef);
          if (courseSnap.exists()) {
            const courseData = courseSnap.data();
            if (courseData.courseContainingModules?.includes(moduleId)) {
              foundCourse = { id: courseSnap.id, ...courseData };
            }
          }
        }

        if (!foundCourse) {
          const coursesRef = await getDocs(query(collection(db, "lms_course")));
          for (const courseDoc of coursesRef.docs) {
            const courseData = courseDoc.data();
            if (courseData.courseContainingModules?.includes(moduleId)) {
              foundCourse = { id: courseDoc.id, ...courseData };
              break;
            }
          }
        }

        if (foundCourse) {
          // Fetch all modules for this course
          const modulePromises = foundCourse.courseContainingModules.map(
            async (modId) => {
              const modRef = doc(db, "lms_module", modId);
              const modSnap = await getDoc(modRef);
              if (modSnap.exists()) {
                return { id: modSnap.id, ...modSnap.data() };
              }
              return null;
            }
          );

          const moduleResults = await Promise.all(modulePromises);
          const validModules = moduleResults.filter((m) => m !== null);
          setAllModules(validModules);

          const currentIndex = validModules.findIndex(
            (mod) => mod.id === moduleId
          );
          setCurrentModuleIndex(currentIndex >= 0 ? currentIndex : 0);

          // Set course data
          setPackageData({
            packageName: foundCourse.courseName,
            packageInstructor: foundCourse.courseInstructor || "Instructor",
          });

          // ✅ Calculate progress based on userData.markAsComplete
          const completedModulesCount = validModules.filter((mod) =>
            userData?.markAsComplete?.includes(mod.id)
          ).length;

          const totalModules = validModules.length;
          const overallProgress =
            totalModules > 0
              ? Math.round((completedModulesCount / totalModules) * 100)
              : 0;

          const currentModule = validModules[currentIndex] || null;
          const currentProgress = currentModule
            ? userData?.markAsComplete?.includes(currentModule.id)
              ? 100
              : 0
            : 0;

          // Keep watchTime as before
          const watchTime = Math.round(Math.random() * 24);

          setUserProgress({
            overall: overallProgress,
            current: currentProgress,
            watchTime: watchTime,
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        notification.error({
          message: "Error loading module",
          placement: "topRight",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [moduleId, navigate, location.search, userData?.markAsComplete]);

  const [currentId, setCurrentId] = useState(null);
  useEffect(() => {
    if (allModules.length > 0 && !currentId) {
      setCurrentId(allModules[0].id);
    }
  }, [allModules, currentId]);

  const markAsComplete = async (moduleId) => {
    try {
      const userRef = doc(db, "users", userData.uid);

      await updateDoc(userRef, {
        markAsComplete: arrayUnion(moduleId),
      });
      await fetchUserData();
      console.log("Module marked as complete!");
    } catch (error) {
      console.error("Error updating:", error);
    }
  };
  const [certificateDate, setCertificateDate] = useState("");

  const getCompletionDate = async (userId, buyItemId) => {
    try {
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        console.error("User not found");
        return;
      }

      const userData = userSnap.data();
      const certificateRecords = userData.certificateRecords || {};

      if (!certificateRecords[buyItemId]) {
        console.log("No completion date found for this course");
        return;
      }

      const completionDate = certificateRecords[buyItemId].completionDate;
      // Convert ISO string to Date object
      setCertificateDate(new Date(completionDate).toLocaleDateString());
    } catch (error) {
      console.error("Error fetching completion date:", error);
    }
  };

  useEffect(() => {
    getCompletionDate(userData.uid, courseId);
  }, [userData.uid, courseId]);

  // Fetch note on mount
  const getNote = async () => {
    const noteRef = doc(db, "users", userData?.uid, "coursesnotes", courseId);
    const noteSnap = await getDoc(noteRef);
    if (noteSnap.exists()) {
      setNote(noteSnap.data().notes || "");
    }
  };
  useEffect(() => {
    getNote();
  }, [userData?.uid, courseId]);

  // Save note
  const handleSave = async () => {
    const noteRef = doc(db, "users", userData?.uid, "coursesnotes", courseId);
    await setDoc(noteRef, { notes: note }, { merge: true });
    getNote();
    notification.success({
      message: "Notes Saved",
      placement: "topRight",
      duration: 3,
    });
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading module...</div>
      </div>
    );
  }

  if (!moduleData) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Module not found</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            {packageData?.packageName || "Course Name"}
          </h1>
          <p className={styles.subtitle}>
            {moduleData?.moduleName || "Module Name"} •{" "}
            {packageData?.packageInstructor || "Instructor"}
          </p>
        </div>
        <div className={styles.headerRight}>
          <span className={styles.lesson}>
            Lesson {currentModuleIndex + 1} of {allModules.length}
          </span>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.videoSection}>
          <div className={styles.videoContainer}>
            {moduleData.moduleVideo ? (
              <video
                controls
                controlsList="nodownload noremoteplayback nofullscreen"
                disablePictureInPicture
                disableRemotePlayback
                onContextMenu={(e) => e.preventDefault()}
                onSelectStart={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                className={styles.video}
                src={moduleData.moduleVideo}
                poster={moduleData.moduleBanner}
                style={{ userSelect: "none", pointerEvents: "auto" }}
                onError={(e) => {
                  console.error("Video load error:", e);
                  notification.error({
                    message: "Video Load Error",
                    description:
                      "Unable to load the video. Please try again later.",
                    placement: "topRight",
                  });
                }}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className={styles.videoPlaceholder}>
                <div className={styles.playButton}>▶</div>
                <div className={styles.videoInfo}>
                  <div className={styles.videoTitle}>
                    {moduleData?.moduleName || "Module Video"}
                  </div>
                  <div className={styles.videoDuration}>
                    Duration: {moduleData?.moduleDuration || "N/A"}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${
                activeTab === "description" ? styles.active : ""
              }`}
              onClick={() => setActiveTab("description")}
            >
              Description
            </button>
            <button
              className={`${styles.tab} ${
                activeTab === "materials" ? styles.active : ""
              }`}
              onClick={() => setActiveTab("materials")}
            >
              Materials
            </button>
            <button
              className={`${styles.tab} ${
                activeTab === "outline" ? styles.active : ""
              }`}
              onClick={() => setActiveTab("outline")}
            >
              Course Outline
            </button>
            <button
              className={`${styles.tab} ${
                activeTab === "notes" ? styles.active : ""
              }`}
              onClick={() => setActiveTab("notes")}
            >
              My Notes
            </button>
          </div>

          <div className={styles.tabContent}>
            {activeTab === "description" && (
              <div>
                <h3 className={styles.contentTitle}>Description</h3>
                <div className={styles.contentText}>
                  {(() => {
                    let description =
                      moduleData?.moduleDescription ||
                      "Module description not available.";

                    // Clean HTML content
                    description = description
                      .replace(/<[^>]*>/g, "") // Remove HTML tags
                      .replace(/&nbsp;/g, " ") // Replace non-breaking spaces
                      .replace(/&amp;/g, "&")
                      .replace(/&lt;/g, "<")
                      .replace(/&gt;/g, ">")
                      .replace(/&quot;/g, '"')
                      .replace(/&#39;/g, "'")
                      .trim();

                    return description;
                  })()}
                </div>

                {moduleData?.moduleObjectives && (
                  <>
                    <h4 className={styles.subTitle}>What you'll learn:</h4>
                    <div className={styles.learningList}>
                      {(() => {
                        let objectives = moduleData.moduleObjectives;
                        // Clean HTML content
                        objectives = objectives
                          .replace(/<[^>]*>/g, "") // Remove HTML tags
                          .replace(/&nbsp;/g, " ") // Replace non-breaking spaces
                          .replace(/&amp;/g, "&")
                          .replace(/&lt;/g, "<")
                          .replace(/&gt;/g, ">")
                          .replace(/&quot;/g, '"')
                          .replace(/&#39;/g, "'")
                          .trim();
                        return objectives;
                      })()}
                    </div>
                  </>
                )}
              </div>
            )}
            {activeTab === "materials" && (
              <div>
                <h3 className={styles.contentTitle}>Materials</h3>
                <p className={styles.contentText}>
                  Course materials and resources will be available here.
                </p>
              </div>
            )}
            {activeTab === "outline" && (
              <div>
                {allModules && allModules.length > 0 ? (
                  <ul className="list-disc pl-6 space-y-4">
                    {allModules.map((item) => (
                      <li key={item.id}>
                        <h3 className="text-lg font-semibold text-gray-800">
                          {item.moduleName || "Course Outline"}
                        </h3>
                        <p
                          className="text-gray-600"
                          dangerouslySetInnerHTML={{
                            __html:
                              item.moduleDescription ||
                              "Complete course outline and structure.",
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">No course outline available.</p>
                )}
              </div>
            )}

            {activeTab === "notes" && (
              <div>
                <h3 className="font-semibold mb-2 text-black">Your Notes</h3>
                <textarea
                  className="w-full border rounded p-2 resize-none text-black dark:text-white bg-white dark:bg-gray-800"
                  rows={5}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Write your course notes here..."
                />
                <button
                  onClick={handleSave}
                  className={`${styles.completeBtn}`}
                >
                  Save Note
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.sidebar}>
          <div className={styles.progressCard}>
            <h3 className={styles.cardTitle}>📊 Course Progress</h3>
            <div className={styles.progressItem}>
              <div className={styles.progressInfo}>
                <span>Overall Progress</span>
                <span>{userProgress.overall}%</span>
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${userProgress.overall}%` }}
                />
              </div>
            </div>
            <div className={styles.progressItem}>
              <div className={styles.progressInfo}>
                <span>Current Lesson</span>
                <span>{userProgress.current}%</span>
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${userProgress.current}%` }}
                />
              </div>
            </div>
            <div className={styles.timeInfo}>
              <span>Last {userProgress.watchTime} hrs watched</span>
            </div>
          </div>

          <div className={styles.lessonsCard}>
            <h3 className={styles.cardTitle}>Lessons</h3>
            <div className={styles.lessonsList}>
              {allModules.map((module, index) => {
                const isCompleted = userData?.markAsComplete?.includes(
                  module.id
                );
                const isActive = currentModuleIndex === index;

                return (
                  <div
                    key={module.id}
                    className={`${styles.lessonItem} ${
                      isCompleted
                        ? styles.completed
                        : isActive
                        ? styles.active
                        : ""
                    }`}
                    onClick={() => {
                      setCurrentId(module.id);
                      navigate(`/student/dashboard/video/${module.id}`);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <span className={styles.checkIcon}>
                      {isCompleted ? "✓" : isActive ? "▶" : "○"}
                    </span>
                    <span>{module.moduleName || `Module ${index + 1}`}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.actionsCard}>
            <h3 className={styles.cardTitle}>Quick Actions</h3>

            <CertificateDownloadButton
              user={userData}
              course={packageData?.packageName}
              courseId={courseId}
              certificateDate={certificateDate}
              disabled={userProgress.overall !== 100}
            />

            {userData?.markAsComplete?.includes(currentId) ? (
              <button
                className={`${styles.completeBtn} ${styles.disabled}`}
                disabled
              >
                ✅ Completed
              </button>
            ) : (
              <button
                onClick={() => markAsComplete(currentId)}
                className={styles.completeBtn}
              >
                Mark as Complete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoView;
